"use client";

import React from "react";
import { cn } from "@/lib/utils";
import Image from "next/image";
import createGlobe from "cobe";
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { IconBrandYoutubeFilled } from "@tabler/icons-react";
import Link from "next/link";

const fadeUpVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

interface Feature {
  title: string;
  description: string;
  skeleton: React.ReactNode;
  className: string;
  ctaText: string;
  ctaLink: string;
}

export function ServicesSection() {
  const features: Feature[] = [
    {
      title: "Managed IT Services",
      description:
        "Technology drives growth and efficiency. Our Managed IT Services handle everything from daily support to long-term planning. We monitor your IT systems to prevent issues, ensuring they run smoothly and securely. Let our experts manage your IT so your team can focus on strategic goals, helping your business scale and stay competitive.",
      skeleton: (
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="w-full lg:flex-1">
            <GlassyCard>
              <ul>
                <FeaturePoint>Device Management & Security</FeaturePoint>
                <FeaturePoint>End-user Support</FeaturePoint>
                <FeaturePoint>
                  Business Continuity & Disaster Recovery
                </FeaturePoint>
                <FeaturePoint>Training & Risk Management</FeaturePoint>
              </ul>
            </GlassyCard>
          </div>
          <div className="w-full lg:flex-1">
            <GlassyCard>
              <ul>
                <FeaturePoint>Mobile Device Management (MDM)</FeaturePoint>
                <FeaturePoint>Business Phone & Broadband</FeaturePoint>
                <FeaturePoint>Network Management & Protection</FeaturePoint>
                <FeaturePoint>Strategic IT Planning</FeaturePoint>
              </ul>
            </GlassyCard>
          </div>
        </div>
      ),
      className: "col-span-1 lg:col-span-4 min-h-[300px]",
      ctaText: "Learn More",
      ctaLink: "/services/managed-it",
    },
    {
      title: "Penetration Testing & Vulnerability Scanning",
      description:
        "Our advanced penetration testing service identifies and helps you fix critical security issues before they can be exploited.",
      skeleton: <GlassySkeletonTwo />,
      className: "col-span-1 lg:col-span-2 min-h-[300px]",
      ctaText: "Protect Your Networks",
      ctaLink: "/services/penetration-testing-vulnerability-scanning",
    },
    {
      title: "Compliance & Risk Management",
      description:
        "CyberGuard Shield is your all-in-one compliance solution. It automates regulatory alignment, streamlines audits, and provides real-time monitoring to ensure your business stays compliant with ever-changing regulations.",
      skeleton: <GlassySkeletonThree />,
      className: "col-span-1 lg:col-span-3 min-h-[300px]",
      ctaText: "Start Your Compliance Journey Now",
      ctaLink: "/services/compliance-risk-management",
    },
    {
      title: "Cloud Computing & CyberSecurity",
      description:
        "As businesses increasingly adopt cloud technologies, ensuring the security of cloud infrastructure becomes paramount. Our Cloud Security services offer advanced measures to protect your cloud environments from threats, ensuring compliance with industry standards and safeguarding your critical data",
      skeleton: <GlassySkeletonFour />,
      className: "col-span-1 lg:col-span-3 min-h-[300px]",
      ctaText: "Learn About Our Cloud Security Measures",
      ctaLink: "/services/cyber-security",
    },
  ];

  return (
    <div className="relative z-20">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="relative"
      >
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-6">
          {features.map((feature) => (
            <motion.div
              key={feature.title}
              variants={fadeUpVariants}
              className={feature.className}
            >
              <div className="relative h-full bg-[rgb(var(--color-primary-dark)_/_0.5)] backdrop-blur-xl rounded-xl border border-[rgb(var(--color-neon-blue)_/_0.1)] shadow-[0_8px_16px_rgba(0,0,0,0.2),_0_0_20px_rgba(var(--color-neon-blue),0.1)] p-6 hover:bg-[rgb(var(--color-primary-dark)_/_0.7)] transition-colors flex flex-col">
                <h3 className="text-xl font-semibold text-white/90 mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-white/70 mb-6">
                  {feature.description}
                </p>
                <div className="flex-1">{feature.skeleton}</div>
                <Link
                  href={feature.ctaLink}
                  className="mt-6 w-full bg-[rgb(var(--color-neon-blue)_/_0.1)] text-[rgb(var(--color-neon-blue-light))] border border-[rgb(var(--color-neon-blue)_/_0.2)] px-4 py-3 rounded-lg text-sm font-medium hover:bg-[rgb(var(--color-neon-blue)_/_0.2)] transition-colors text-center"
                >
                  {feature.ctaText}
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

const GlassyCard = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-[rgb(var(--color-primary-dark)_/_0.3)] backdrop-blur-xl rounded-xl border border-[rgb(var(--color-neon-blue)_/_0.1)] p-6 h-full">
      {children}
    </div>
  );
};

const FeaturePoint = ({ children }: { children: React.ReactNode }) => {
  return (
    <li className="flex items-start gap-3 mb-3">
      <svg
        className="h-4 w-4 flex-shrink-0 text-[rgb(var(--color-neon-blue))] mt-0.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path d="M5 13l4 4L19 7"></path>
      </svg>
      <span className="text-sm leading-relaxed text-white/70">{children}</span>
    </li>
  );
};

// Keep the rest of your skeleton components but update their styling
export const GlassySkeletonOne = () => {
  return (
    <div className="relative flex py-8 px-2 gap-10 h-full">
      <GlassyCard>
        <ul>
          <FeaturePoint>Real-time issue tracking</FeaturePoint>
          <FeaturePoint>Customizable workflows</FeaturePoint>
          <FeaturePoint>Advanced filtering and sorting</FeaturePoint>
          <FeaturePoint>Automated notifications</FeaturePoint>
        </ul>
      </GlassyCard>
    </div>
  );
};

export const GlassySkeletonThree = () => {
  return (
    // <Link
    //   href="https://www.youtube.com/watch?v=RPa3_AD1_Vs"
    //   target="__blank"
    //   className="relative flex gap-10 h-full"
    // >
    <GlassyCard>
      <ul>
        <FeaturePoint>Comprehensive Baseline Assessment</FeaturePoint>
        <FeaturePoint> Active NIS2 Expertise</FeaturePoint>
        <FeaturePoint> Dynamic Tailored Solutions</FeaturePoint>
        <FeaturePoint>Automated Reporting</FeaturePoint>
      </ul>
      {/* <IconBrandYoutubeFilled className="h-16 w-16 absolute right-4 bottom-4 text-purple-300/50" /> */}
    </GlassyCard>
    // </Link>
  );
};

export const GlassySkeletonTwo = () => {
  return (
    <div className="relative flex flex-col h-full">
      <GlassyCard>
        <ul>
          <FeaturePoint>Network vulnerability assessment</FeaturePoint>
          <FeaturePoint>Digital footprint protection</FeaturePoint>
          <FeaturePoint>System security hardening</FeaturePoint>
          <FeaturePoint>Internal threat detection</FeaturePoint>
        </ul>
      </GlassyCard>
    </div>
  );
};

export const GlassySkeletonFour = () => {
  return (
    <div className="relative flex flex-col h-full">
      <GlassyCard>
        <ul>
          <FeaturePoint>Cloud infrastructure management</FeaturePoint>
          <FeaturePoint>Identity and access management</FeaturePoint>
          <FeaturePoint>Continuous threat monitoring</FeaturePoint>
          <FeaturePoint>Seamless migration services</FeaturePoint>
        </ul>
      </GlassyCard>
      <Globe className="absolute -right-10 -bottom-40 opacity-30" />
    </div>
  );
};

export const Globe = ({ className }: { className?: string }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const globeRef = useRef<ReturnType<typeof createGlobe> | null>(null);
  const phiRef = useRef(0);
  const animationFrameRef = useRef<number | null>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    if (!canvasRef.current) return;

    globeRef.current = createGlobe(canvasRef.current, {
      devicePixelRatio: 2,
      width: 400 * 2,
      height: 400 * 2,
      phi: 0,
      theta: 0,
      dark: 1,
      diffuse: 1.2,
      mapSamples: 16000,
      mapBrightness: 6,
      baseColor: [0.3, 0.3, 0.3],
      markerColor: [0.1, 0.8, 1],
      glowColor: [1, 1, 1],
      markers: [
        { location: [37.7595, -122.4367], size: 0.03 },
        { location: [40.7128, -74.006], size: 0.1 },
      ],
      onRender: (state) => {
        state.phi = phiRef.current;
      },
    });

    return () => {
      globeRef.current?.destroy();
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    observer.observe(canvas);

    return () => {
      observer.unobserve(canvas);
    };
  }, []);

  useEffect(() => {
    const animate = () => {
      if (!isIntersecting || !globeRef.current) {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
          animationFrameRef.current = null;
        }
        return;
      }

      phiRef.current += 0.005;
      if (globeRef.current.canvas) {
        // A bit hacky: Access internal state if needed and if possible,
        // or just rely on COBE's internal loop if it exists and onRender is called.
        // For now, we assume setting phiRef and COBE reading it in its loop is sufficient.
        // Let's try triggering onRender manually if needed.
        // globeRef.current.onRender(globeRef.current.options); // This might work depending on COBE implementation
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    if (isIntersecting) {
      if (!animationFrameRef.current) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isIntersecting]);

  return (
    <canvas
      ref={canvasRef}
      style={{ width: 400, height: 400, maxWidth: "100%", aspectRatio: 1 }}
      className={className}
    />
  );
};
